import { useState } from 'react';
import { useEditorStore } from '@/stores/editorStore';
import { RotateCcw, Sun, Moon, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PaneMaximizeButton, PaneCollapseButton } from './PaneControls';
import { resolveStarter } from './starter';
import type { Problem } from '@/lib/api/problems';

interface EditorToolbarProps {
  slug: string;
  problem: Problem;
  languages: { value: string; label: string }[];
  isMaximized: boolean;
  onToggleMaximize: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const FONT_SIZES = [12, 13, 14, 15, 16, 18, 20];

export function EditorToolbar({
  slug,
  problem,
  languages,
  isMaximized,
  onToggleMaximize,
  isCollapsed,
  onToggleCollapse,
}: EditorToolbarProps) {
  const { language, setLanguage, theme, setTheme, fontSize, setFontSize, resetCode } =
    useEditorStore();
  const [resetOpen, setResetOpen] = useState(false);

  const handleReset = () => {
    // Resolve the same starter as initial load (function stub for function-mode,
    // else the default template) — not problem.starter_code, which is empty for
    // converted problems and would reset the editor to blank.
    resetCode(slug, language, resolveStarter(problem, language));
    setResetOpen(false);
  };

  return (
    <div className="flex items-center justify-between gap-1 border-b border-border bg-card px-2 py-1.5">
      {/* Left: language selector */}
      <div className="flex shrink-0 items-center gap-2">
        <Select value={language} onValueChange={setLanguage}>
          {/* An outlined chip rather than borderless text. The language is a
              control a student changes, and borderless it read as a label. */}
          <SelectTrigger
            className="h-7 w-auto gap-1.5 border border-border bg-background px-2.5 text-[13px] font-semibold shadow-none hover:bg-muted focus:ring-0 focus:ring-offset-0"
            aria-label="Programming language"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.value} value={lang.value}>
                {lang.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Center: Editor controls */}
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setResetOpen(true)}
          title="Reset to starter code"
          aria-label="Reset to starter code"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
        <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset code to starter template?</AlertDialogTitle>
              <AlertDialogDescription>
                Your changes will be lost. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleReset}>Reset</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setTheme(theme === 'vs-dark' ? 'light' : 'vs-dark')}
          title="Toggle theme"
          aria-label="Toggle theme"
        >
          {theme === 'vs-dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
        </Button>
        <div className="hidden sm:block">
          {/* Icon-only: "14px" in a 70px combobox spent more room than a
              rarely-changed preference deserves, and sat oddly among four
              icon buttons. The current size still shows in the open menu. */}
          <Select value={String(fontSize)} onValueChange={(v) => setFontSize(Number(v))}>
            <SelectTrigger
              className="h-7 w-7 justify-center border-0 bg-transparent p-0 shadow-none hover:bg-muted focus:ring-0 focus:ring-offset-0 [&>svg:last-child]:hidden"
              aria-label={`Text size, currently ${fontSize} pixels`}
              title="Text size"
            >
              <Type className="h-3.5 w-3.5" />
            </SelectTrigger>
            <SelectContent>
              {FONT_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}px
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
        {onToggleCollapse && (
          <PaneCollapseButton isCollapsed={!!isCollapsed} onToggle={onToggleCollapse} />
        )}
        <PaneMaximizeButton isMaximized={isMaximized} onToggle={onToggleMaximize} label="editor" />
      </div>
    </div>
  );
}
